import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';

// Concise educational section explaining the tester/ECU request-response flow that
// produces a Negative Response Code, with the worked example from the spec.
const HowNrcWorks = () => (
  <Card className="technical-card" data-testid="nrc-how-it-works">
    <CardHeader>
      <CardTitle className="font-chivo uppercase tracking-tight text-base">How UDS Negative Responses Work</CardTitle>
      <CardDescription>The request/response flow that produces an NRC, in one worked example.</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4 text-sm leading-relaxed text-foreground/90">
      <div className="flex flex-wrap items-center gap-2 font-mono text-xs md:text-sm bg-muted/40 rounded-md px-4 py-3">
        <span className="font-semibold">Tester</span>
        <span className="text-muted-foreground">&rarr; UDS Request &rarr;</span>
        <span className="font-semibold">ECU</span>
        <span className="text-muted-foreground">&rarr;</span>
        <span className="font-semibold">Positive Response</span>
        <span className="text-muted-foreground">/</span>
        <span className="font-semibold">Negative Response (NRC)</span>
      </div>

      <p>
        A UDS tester sends a service request (Service ID, optionally a sub-function and
        parameters). If the ECU can carry it out, it replies with a positive response.
        If it cannot — for any reason from an unsupported service to a wrong diagnostic
        session — it replies with a negative response: a fixed <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">0x7F</code> marker
        byte, an echo of the requested Service ID, and a one-byte Negative Response Code
        (NRC) explaining why.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-md border border-structure p-3 space-y-1">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Request</p>
          <p className="font-mono text-sm">22 F1 90</p>
          <p className="text-xs text-muted-foreground">Read Data By Identifier (0x22) for DID 0xF190 (VIN).</p>
        </div>
        <div className="rounded-md border border-structure p-3 space-y-1">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Negative Response</p>
          <p className="font-mono text-sm">7F 22 31</p>
          <p className="text-xs text-muted-foreground">0x7F marker, echoing SID 0x22, with NRC 0x31 — Request Out Of Range (this DID isn&apos;t supported, or isn&apos;t available in the current state).</p>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Try either line above in the decoder: paste the full response (<code className="font-mono">7F 22 31</code>) to see the byte breakdown, or just the NRC (<code className="font-mono">31</code>) to see the code&apos;s meaning on its own.
      </p>
    </CardContent>
  </Card>
);

export default HowNrcWorks;
